import React, { Component } from "react";
import PropTypes from "prop-types";
import Konva from "konva";
import WheelItem from "./WheelItem";
import NameRoulette from "./NameRoulette";
import WheelLever from "./WheelLever";
import WheelArrow from "./WheelArrow";
import WheelSpinner from "./WheelSpinner";
import { getStoredUsedGlobal, getStoredCategories } from "../utils/storage";

const getUsedQuestionsByCategory = (usedQuestions) => {
    if (
        usedQuestions &&
        typeof usedQuestions === "object" &&
        !Array.isArray(usedQuestions)
    ) {
        return usedQuestions;
    }

    return {};
};

const getPriorityQuestionsForSectors = (category, selectedSectors = []) => {
    const priorityQuestions = Array.isArray(category?.priority_questions)
        ? category.priority_questions
        : [];

    if (!priorityQuestions.length) {
        return [];
    }

    const sectorIds = (selectedSectors || [])
        .map(Number)
        .filter((id) => !Number.isNaN(id));

    if (!sectorIds.length) {
        return priorityQuestions;
    }

    return priorityQuestions.filter((question) =>
        (question.sector_ids || [])
            .map(Number)
            .some((sectorId) => sectorIds.includes(sectorId)),
    );
};

const getUsedQuestionIds = (category, usedByCategory, teamMember) => {
    const usedForCategory =
        usedByCategory?.[category?.category_id] ||
        usedByCategory?.[String(category?.category_id)] ||
        [];

    if (Array.isArray(usedForCategory)) {
        return usedForCategory.map(Number);
    }

    if (usedForCategory && typeof usedForCategory === "object") {
        const memberKey =
            teamMember != null && teamMember !== "" ? String(teamMember) : "";
        const memberUsed =
            usedForCategory[memberKey] ||
            usedForCategory[String(memberKey)] ||
            [];

        return Array.isArray(memberUsed) ? memberUsed.map(Number) : [];
    }

    return [];
};

const categoryHasUnusedPriorityQuestions = (
    category,
    usedByCategory,
    selectedSectors,
    teamMember,
) => {
    const relevantPriority = getPriorityQuestionsForSectors(
        category,
        selectedSectors,
    );

    if (!relevantPriority.length) {
        return false;
    }

    const usedIds = getUsedQuestionIds(category, usedByCategory, teamMember);

    return relevantPriority.some(
        (question) => !usedIds.includes(Number(question.question_id)),
    );
};

const categoryHasBeenShown = (category, usedByCategory, teamMember) => {
    return getUsedQuestionIds(category, usedByCategory, teamMember).length > 0;
};

const pickRandomCategoryId = (pool) => {
    if (!Array.isArray(pool) || pool.length === 0) {
        return null;
    }

    const randomIndex = Math.floor(Math.random() * pool.length);
    const categoryId = pool[randomIndex]?.category_id;

    return categoryId == null ? null : categoryId.toString();
};

/**
 * Prefer question_category terms that still have unused priority questions.
 * After those are all shown, prefer the remaining (non-priority) categories.
 */
const pickPreferredCategoryId = (
    categories,
    usedQuestions,
    selectedSectors,
    teamMember,
) => {
    const list = Array.isArray(categories) ? categories : [];

    if (!list.length) {
        return null;
    }

    const usedByCategory = getUsedQuestionsByCategory(usedQuestions);

    const unusedPriority = list.filter((category) =>
        categoryHasUnusedPriorityQuestions(
            category,
            usedByCategory,
            selectedSectors,
            teamMember,
        ),
    );

    if (unusedPriority.length) {
        return pickRandomCategoryId(unusedPriority);
    }

    const unshownOther = list.filter(
        (category) =>
            getPriorityQuestionsForSectors(category, selectedSectors).length ===
                0 &&
            !categoryHasBeenShown(category, usedByCategory, teamMember),
    );

    if (unshownOther.length) {
        return pickRandomCategoryId(unshownOther);
    }

    return pickRandomCategoryId(list);
};

// The pick-stop is the top of the wheel (12 o'clock).
// Konva: 0° is 3 o'clock, clockwise, so top is 270°.
const POINTER_ANGLE = 270;

const getPickStopPoint = (diameter) => {
    return {
        x: diameter / 2,
        y: 4,
    };
};

const getCategoryIdAtPickStop = (layer, diameter) => {
    try {
        if (!layer || typeof layer.getIntersection !== "function") {
            return null;
        }

        const selectedElement = layer.getIntersection(
            getPickStopPoint(diameter),
        );

        if (selectedElement && selectedElement.attrs?.name) {
            return selectedElement.attrs.name.toString();
        }
    } catch (err) {
        console.error("Intersection check failed:", err);
    }

    return null;
};

const getSliceAngle = (totalItems) => {
    return totalItems > 0 ? 360 / totalItems : 360;
};

const getCategoryIndexAtPointer = (rotation, totalItems) => {
    if (!totalItems) {
        return 0;
    }

    const sliceAngle = getSliceAngle(totalItems);
    const localPointer = (((POINTER_ANGLE - rotation) % 360) + 360) % 360;
    let index = Math.floor(localPointer / sliceAngle) - 1;

    if (index < 0) {
        index += totalItems;
    }

    return index % totalItems;
};

const getLandingRotation = (
    currentRotation,
    targetIndex,
    totalItems,
    extraTurns,
) => {
    const sliceAngle = getSliceAngle(totalItems);
    // WheelItem index is 1-based, so slice i starts at sliceAngle * (i + 1)
    const landingAngle = POINTER_ANGLE - sliceAngle * (targetIndex + 1.5);
    const currentNorm = ((currentRotation % 360) + 360) % 360;
    const landingNorm = ((landingAngle % 360) + 360) % 360;
    let delta = landingNorm - currentNorm;

    if (delta <= 0) {
        delta += 360;
    }

    return currentRotation + delta + 360 * extraTurns;
};

const easeOutCubic = (t) => {
    return 1 - Math.pow(1 - t, 3);
};

/**
 * TODO:  Exposed API :
 * 1. Diameter ✔️
 * 2. Maximum Duration ✔️
 * 3. onSelect callback ✔️
 *
 * Feature:
 * 1. Responsive ✔️
 *
 * @augments {Component<Wheel.propTypes, {}>}
 */
class Wheel extends Component {
    static propTypes = {
        // Overall canvas diameter. Default is 500
        diameter: PropTypes.number,

        // Maximum duration for the spinner (in milliseconds). Default is 5 second
        maximumDuration: PropTypes.number,

        // Maximum duration for the spinner (in milliseconds). Default is 300ms
        minimumDuration: PropTypes.number,

        // The category data
        data: PropTypes.array.isRequired,

        /**
         * Callback when the spinner is stop, and get the selected category, it
         * will pass parameter one parameter (name)
         */
        onSelectedPlayer: PropTypes.func.isRequired,
        onSelect: PropTypes.func.isRequired,
        buttonText: PropTypes.string,
        buttonLogo: PropTypes.string,
        buttonColor: PropTypes.string,
        buttonBg: PropTypes.string,
        textStyle: PropTypes.object,
    };

    static defaultProps = {
        diameter: 500,
        maximumDuration: 3000,
        minimumDuration: 300,
        onSelect: (id) => {},
        buttonLogo: "redo-alt",
        buttonText: "Spin",
        isCustomLogo: false,
        onSelectedPlayer: (name) => {},
    };

    constructor(props) {
        super(props);
        this.state = {
            rotation: 0,
            diameter: props.diameter,
            isSpinning: false,
            duration: 0,
            isLevel: "",
            isVertical: false,
        };

        this.wrapper = React.createRef();
        this.animateLayer = React.createRef();
        this.animateGroup = React.createRef();
        this.forceLever = React.createRef();
    }

    registerTickSound =
        typeof Audio !== "undefined"
            ? new Audio(
                  "https://res.cloudinary.com/drtnbid6h/video/upload/v1641880582/tick_mpn0xo.mp3",
              )
            : null;

    playTick = () => {
        try {
            if (this.registerTickSound) {
                const sound = this.registerTickSound.cloneNode();
                const playPromise = sound.play();
                if (playPromise !== undefined) {
                    playPromise.catch(() => {});
                }
            }
        } catch (e) {
            try {
                if (this.registerTickSound) {
                    this.registerTickSound.currentTime = 0;
                    this.registerTickSound.play().catch(() => {});
                }
            } catch (err) {}
        }
    };

    componentDidMount() {
        this.updateDiameter();
        window.addEventListener("resize", this.updateDiameter);

        if (this.props.data.length > 6) {
            this.setState({
                isVertical: true,
            });
        } else {
            this.setState({
                isVertical: false,
            });
        }
    }

    componentWillUnmount() {
        window.removeEventListener("resize", this.updateDiameter);
    }

    updateDiameter = () => {
        /**
         * configDiameter is the desired diameter that passed via props, not from
         * current state, if the desired diameter is too big then resize canvas to
         * maximum possible layout instead
         */

        const configDiameter = this.props.diameter;
        const wrapperSize = this.wrapper.current
            ? this.wrapper.current.clientWidth
            : 0;

        if (wrapperSize === 0) {
            const newDiameter = 500;
            this.setState({
                diameter: newDiameter,
                isVisualCheck: true,
            });
        } else {
            const newDiameter = Math.min(configDiameter, wrapperSize);
            const shouldUpdate = newDiameter !== this.state.diameter;

            if (shouldUpdate) {
                this.setState({
                    diameter: newDiameter,
                    isVisualCheck: true,
                });
            }
        }
    };

    spin = () => {
        const { offsetLeft } = this.forceLever.current;
        let forcePower = offsetLeft / 100;
        let isLevel = "";

        if (offsetLeft <= 5) {
            forcePower = 0.2;
            isLevel = "LOW";
        } else if (offsetLeft >= 6 && offsetLeft <= 20) {
            forcePower = 0.5;
            isLevel = "MEDIUM";
        } else if (offsetLeft >= 21 && offsetLeft <= 40) {
            forcePower = 1;
            isLevel = "HIGH";
        } else if (offsetLeft >= 41 && offsetLeft <= 50) {
            forcePower = 0.5;
            isLevel = "MEDIUM";
        } else if (offsetLeft >= 50) {
            forcePower = 0.2;
            isLevel = "LOW";
        }

        const animateLayer = this.animateLayer.current;
        const animateGroup = this.animateGroup;
        const props = this.props;
        const thisSetState = (params) => this.setState({ ...params });
        const playTick = this.playTick;

        // const speedForce = 5 + 10 * random; //old random speedforce
        const speedForce = 5 + 10 * forcePower;

        const _randomDuration = (speedForce * 1000) / 2;
        const _maxDuration = props.maximumDuration;
        const _minDuration = props.minimumDuration;

        // Make sure the duration is not longer than the maximum limit
        const _notTooLong = Math.min(_randomDuration, _maxDuration);

        // Make sure the duration is not too short than the minimum limit
        const _notTooShort = Math.max(_notTooLong, _minDuration);

        const duration = _notTooShort;

        // set isSpinning, duration, and isLevel in one state update
        this.setState({
            isSpinning: true,
            duration: duration,
            ...(isLevel ? { isLevel } : {}),
        });

        const categories = this.props.data;
        const totalItems = categories.length;
        const diameter = this.state.diameter;
        const startRotation = animateGroup.current
            ? animateGroup.current.rotation()
            : 0;
        const targetCategoryId = pickPreferredCategoryId(
            categories,
            getStoredUsedGlobal([]),
            getStoredCategories([]),
            this.props.result,
        );
        const targetIndex = categories.findIndex(
            (category) =>
                String(category.category_id) === String(targetCategoryId),
        );
        const extraTurns = 2 + Math.round(forcePower * 3);
        const endRotation =
            targetIndex >= 0
                ? getLandingRotation(
                      startRotation,
                      targetIndex,
                      totalItems,
                      extraTurns,
                  )
                : startRotation + 360 * extraTurns;

        let activeIndex = getCategoryIndexAtPointer(startRotation, totalItems);

        const animationPromise = new Promise((resolve) => {
            const animation = new Konva.Animation(function (frame) {
                const time = frame.time;

                if (time < duration && animateGroup.current) {
                    const progress = easeOutCubic(Math.min(time / duration, 1));
                    const currentRotation =
                        startRotation +
                        (endRotation - startRotation) * progress;
                    animateGroup.current.rotation(currentRotation);

                    const newIndex = getCategoryIndexAtPointer(
                        currentRotation,
                        totalItems,
                    );

                    if (newIndex !== activeIndex) {
                        activeIndex = newIndex;
                        playTick();
                    }

                    return;
                }

                this.stop();

                if (animateGroup.current) {
                    animateGroup.current.rotation(endRotation);
                }

                if (animateLayer && typeof animateLayer.draw === "function") {
                    animateLayer.draw();
                }

                const stoppedId = getCategoryIdAtPickStop(
                    animateLayer,
                    diameter,
                );
                const selectedId =
                    stoppedId ||
                    (targetIndex >= 0
                        ? categories[targetIndex].category_id?.toString()
                        : categories[0]?.category_id?.toString());

                thisSetState({ isSpinning: false });
                resolve(props.onSelect(selectedId));
            }, animateLayer);
            animation.start();
        });

        return animationPromise;
    };

    preventMoreThanItShould = () => {
        if (this.props.currentTime <= 0) {
            this.props.isMaxAlert();
        } else {
            this.spin();
        }
    };

    render() {
        const items = this.props.data;
        const totalItems = items.length;

        /**
         * Overall diameter of a spinner, everything attached to this size, so if
         * you change this it will also maintain the ratio of all the item inside
         * the canvas
         */
        const diameter = this.state.diameter;
        const radius = diameter / 2;

        const isVertical = this.state.isVertical;

        const renderWheelItems = items.map((item, index) => (
            <WheelItem
                key={index}
                diameter={diameter}
                radius={radius}
                totalItems={totalItems}
                index={index + 1}
                data={item}
                textStyle={this.props.textStyle}
                isVertical={isVertical}
            />
        ));

        return (
            <>
                <div
                    className="v2-wheel"
                    ref={this.wrapper}
                    style={{ width: diameter }}
                >
                    <div className="v2-wheel-left-side">
                        <NameRoulette
                            duration={this.state.duration}
                            members={this.props.members}
                            onSelectedPlayer={this.props.onSelectedPlayer}
                            isSpinning={this.state.isSpinning}
                            result={this.props.result}
                        />
                        <WheelArrow isSpinning={this.state.isSpinning} />
                    </div>
                    <WheelSpinner
                        diameter={diameter}
                        isSpinning={this.state.isSpinning}
                        animateLayer={this.animateLayer}
                        radius={radius}
                        animateGroup={this.animateGroup}
                        renderWheelItems={renderWheelItems}
                    />
                    <WheelLever
                        className={this.state.isSpinning ? "active" : ""}
                        onClick={this.preventMoreThanItShould}
                        disabled={this.state.isSpinning}
                        isSpinning={this.state.isSpinning}
                        isLevel={this.state.isLevel}
                        isOpenQuestion={this.props.isOpenQuestion}
                        ref={this.forceLever}
                    />
                </div>
            </>
        );
    }
}

export default Wheel;
